/*
 * "Convert Image to Chart" — in-pane sheet (SHEET placement, not a modal).
 *
 * Docks at the bottom of the CHAT tab above the input bar, the same placement
 * ConvertToInfographicSheet uses. Its chrome lives in
 * ../../shared/styles/inPaneSheet.module.css — a copy of that sheet's styling
 * rather than a shared import, so this feature cannot restyle Infographic by
 * accident. The two are worth de-duplicating once a third sheet exists and the
 * owning team can review the change.
 *
 * Opened by the `imageToChart` action chip once a picture of a chart is
 * selected on the slide.
 *
 * The approved layout, top to bottom: the twelve-tile Chart Type grid, the
 * "AI read the image: …" banner, an optional free-text description, then
 * Cancel / Create Chart. The banner's copy is generated from the API's own
 * answer, so it reports what was actually extracted rather than a fixed
 * sentence, and the preview under it draws that same data.
 *
 * The sheet renders only its two LIVE states, per §5 of the coding standards:
 * reading (progress row, tiles disabled) and ready (banner + preview). Every
 * way a run can END — inserted, failed, stopped — closes the sheet and reports
 * through the chat toaster instead, the way the rest of the add-in reports the
 * result of an action. Cancel closes with no chat entry and no deck change at
 * any point.
 */

import { useCallback, useEffect, type ChangeEvent } from "react";

// Host-agnostic despite the folder name: this is the app's only toast bridge,
// and its sink is the chat panel's <ChatToaster>.
import { emitToast, type ToastPayload } from "../../../../../../integrations/tools/outlook/toast-bridge";
import { useModalDismiss } from "../../ColorTools/hooks/useModalDismiss";
import {
  CHART_CREATING_LABEL,
  CHART_READING_LABEL,
  isConvertChartBusy,
  useConvertImageToChart,
  type ConvertChartState,
} from "../hooks/useConvertImageToChart";
import { CHART_TYPE_OPTIONS, type ChartTypeOption, type ChartTypeOptionId } from "../services/chartTypeOptions";
import { MAX_CHART_HINT_CHARS, shownWarnings, type EditableChart } from "../services/convertChartApi";
import { CHART_TYPE_ICONS } from "./chartTypeIcons";
import { ChartPreview } from "./ChartPreview";
import { chartSummaryLine, insertedChartMessage } from "./chartSummary";
import sheet from "../../shared/styles/inPaneSheet.module.css";
import styles from "./ConvertImageToChartSheet.module.css";

interface ConvertImageToChartSheetProps {
  readonly open: boolean;
  /** Closes the sheet. Called by Cancel, the close (x) button, Escape, an
   *  outside click, and once a chart has been created. */
  readonly onClose: () => void;
}

const SHEET_TITLE = "Convert Image to Chart";
const DESCRIPTION_PLACEHOLDER = "e.g., Revenue by region, FY24 vs FY25";
const CANCELLED_MESSAGE = "Reading the image was stopped. Nothing was changed.";

/**
 * The toast a terminal state reports, or `null` while the run is still live.
 *
 * Stopping is deliberately `info`, not `error`: the user pressed Stop, so the
 * only thing worth confirming is that the deck was left alone.
 */
function terminalOutcome(state: ConvertChartState): ToastPayload | null {
  if (state.status === "done") {
    return { title: insertedChartMessage(state.summary), intent: "success" };
  }
  if (state.status === "error") return { title: state.message, intent: "error" };
  if (state.status === "cancelled") return { title: CANCELLED_MESSAGE, intent: "info" };
  return null;
}

export function ConvertImageToChartSheet({ open, onClose }: Readonly<ConvertImageToChartSheetProps>) {
  const controller = useConvertImageToChart(open);
  // Destructured, not read off `controller` inside the callbacks: the
  // controller object is new on every render, so depending on it would give
  // each tile a new onSelect every keystroke in the description field.
  const { state, selectedOption, description, chart, warnings, setDescription, selectOption } = controller;
  const sheetRef = useModalDismiss<HTMLDialogElement>(open, onClose);

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value),
    [setDescription],
  );

  // Every terminal outcome — inserted, failed, stopped — closes the sheet and
  // reports through the chat toaster, the way the rest of the add-in reports
  // the result of an action (see useActionChipDispatch). Three reasons it is
  // not an inline panel:
  //
  //  - On success the result is on the slide, so a sheet left open over it
  //    hides the one thing the user asked for.
  //  - On failure there is nothing left to review — the tile grid and preview
  //    describe an extraction that didn't happen.
  //  - A failure message from the vision model can run to a paragraph, which
  //    reflows the whole sheet; a toast is a fixed, dismissible line.
  //
  // Reported here rather than in the hook so the hook stays free of UI side
  // effects and testable without a toast sink.
  useEffect(() => {
    const outcome = terminalOutcome(state);
    if (!outcome) return;
    emitToast(outcome);
    onClose();
  }, [state, onClose]);

  if (!open) return null;

  const busy = isConvertChartBusy(state);

  return (
    <dialog open className={sheet.sheet} ref={sheetRef} aria-modal="false" aria-label={SHEET_TITLE}>
      <div className={sheet.header}>
        <span className={sheet.title}>{SHEET_TITLE}</span>
        <button
          type="button"
          className={sheet.closeBtn}
          onClick={onClose}
          aria-label="Close"
          data-testid="convert-chart-close"
        >
          ×
        </button>
      </div>

      <div className={sheet.body}>
        <span className={sheet.fieldLabel} id="convert-chart-type-label">
          Chart Type
        </span>
        <div className={styles.typeGrid} role="radiogroup" aria-labelledby="convert-chart-type-label">
          {CHART_TYPE_OPTIONS.map((option) => (
            <ChartTypeTile
              key={option.id}
              option={option}
              selected={option.id === selectedOption.id}
              disabled={busy}
              onSelect={selectOption}
            />
          ))}
        </div>

        <SheetStatus state={state} chart={chart} option={selectedOption} warnings={warnings} />

        <label className={sheet.fieldLabel} htmlFor="convert-chart-description">
          Describe your visualization (optional)
        </label>
        <textarea
          id="convert-chart-description"
          className={sheet.textarea}
          rows={2}
          maxLength={MAX_CHART_HINT_CHARS}
          placeholder={DESCRIPTION_PLACEHOLDER}
          value={description}
          onChange={handleDescriptionChange}
          disabled={busy}
          data-testid="convert-chart-description-input"
        />
      </div>

      <div className={sheet.footer}>
        <button
          type="button"
          className={sheet.cancelBtn}
          onClick={busy ? controller.cancel : onClose}
          data-testid="convert-chart-cancel"
        >
          {busy ? "Stop" : "Cancel"}
        </button>
        <button
          type="button"
          className={sheet.createBtn}
          onClick={controller.createChart}
          disabled={state.status !== "ready" || chart === null}
          data-testid="convert-chart-create"
        >
          {state.status === "creating" ? "Creating…" : "Create Chart"}
        </button>
      </div>
    </dialog>
  );
}

function ChartTypeTile({
  option,
  selected,
  disabled,
  onSelect,
}: Readonly<{
  option: ChartTypeOption;
  selected: boolean;
  disabled: boolean;
  onSelect: (id: ChartTypeOptionId) => void;
}>) {
  const Icon = CHART_TYPE_ICONS[option.id];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={selected ? `${styles.tile} ${styles.tileSelected}` : styles.tile}
      onClick={() => onSelect(option.id)}
      disabled={disabled}
      data-testid={`chart-type-${option.id}`}
    >
      <span className={styles.tileIcon}>
        <Icon />
      </span>
      <span className={styles.tileLabel}>{option.label}</span>
    </button>
  );
}

/**
 * The band between the tile grid and the description: whichever live state the
 * run is in. Split out of the sheet so the states read as a list rather than as
 * conditionals threaded through the layout.
 *
 * The terminal states render nothing — the sheet's effect closes it and reports
 * through
 * the toaster, so a success banner here would only flash.
 */
function SheetStatus({
  state,
  chart,
  option,
  warnings,
}: Readonly<{
  state: ConvertChartState;
  chart: EditableChart | null;
  option: ChartTypeOption;
  warnings: readonly string[];
}>) {
  if (state.status === "extracting" || state.status === "creating") {
    return (
      // <output> has an implicit role of "status", and is announced by screen
      // readers on hosts where an explicit role="status" is not.
      <output className={styles.progress} data-testid="convert-chart-progress">
        <span className={styles.spinner} aria-hidden="true" />
        {state.status === "extracting" ? CHART_READING_LABEL : CHART_CREATING_LABEL}
      </output>
    );
  }

  if (state.status !== "ready" || !chart) return null;

  // Caveats are the vision model's prose, so they are shaped and capped before
  // they reach the sheet — an extraction can report a dozen, at any length.
  const shown = shownWarnings(warnings);

  return (
    <>
      <p className={styles.banner} data-testid="convert-chart-banner">
        {chartSummaryLine(state.result, option.label)}
      </p>
      <ChartPreview chart={chart} render={option.render} typeLabel={option.label} />
      {shown.length > 0 && (
        <ul className={styles.warnings} data-testid="convert-chart-warnings">
          {shown.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </>
  );
}
